import { CheckCircle, Code, Copy, Download, Key, Link2, Mail, MessageCircle, MessageSquare, Plus, RefreshCw, Smartphone, Slack, Trash2, XCircle } from 'lucide-react';
import { formatDate } from '../../utils/datetime';
import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, InputGroup,
  Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import APICall from '../../APICalls/APICall';
import useMetaEmbeddedSignup, { FEATURE_COEXISTENCE } from './useMetaEmbeddedSignup';
import TabComponent from '../../components/TabComponent';
import { useToast } from '../../components/useToast';
import { CHAT_SERVICE_URL } from '../../config/endpoints';

const CHAT_SVC = CHAT_SERVICE_URL;

/* ── Channel metadata ─────────────────────────────────────── */
const CHANNELS = {
  whatsapp: {
    label:   'WhatsApp Business',
    icon:    Smartphone,
    color:   '#25d366',
    fields: [
      { key: 'phone_number_id',     label: 'Phone Number ID',     type: 'text',     help: 'Found in Meta Business → WhatsApp → Phone Numbers' },
      { key: 'access_token',        label: 'Access Token',        type: 'password', help: 'Permanent System User access token from Meta' },
      { key: 'verify_token',        label: 'Verify Token',        type: 'text',     help: 'A secret string you choose — paste this in Meta console too' },
      { key: 'business_account_id', label: 'Business Account ID', type: 'text',     help: 'Your WhatsApp Business Account ID' },
      { key: 'app_secret',          label: 'App Secret',          type: 'password', help: 'Meta App → Settings → Basic → App Secret. Verifies inbound webhook signatures.' },
    ],
    webhook: `${CHAT_SVC}/chat/webhooks/whatsapp`,
  },
  facebook: {
    label:   'Facebook Messenger',
    icon:    MessageCircle,
    color:   '#1877f2',
    fields: [
      { key: 'page_id',           label: 'Page ID',             type: 'text',     help: 'Your Facebook Page ID' },
      { key: 'page_access_token', label: 'Page Access Token',   type: 'password', help: 'Generated in Facebook Developer console' },
      { key: 'verify_token',      label: 'Verify Token',        type: 'text',     help: 'A secret string you choose' },
      { key: 'app_secret',        label: 'App Secret',          type: 'password', help: 'Meta App → Basic Information → App Secret' },
    ],
    webhook: `${CHAT_SVC}/chat/webhooks/facebook`,
  },
  slack: {
    label:   'Slack',
    icon:    Slack,
    color:   '#4a154b',
    fields: [
      { key: 'bot_token',      label: 'Bot Token',          type: 'password', help: 'Starts with xoxb- from Slack App OAuth settings' },
      { key: 'signing_secret', label: 'Signing Secret',     type: 'password', help: 'Slack App → Basic Information → Signing Secret' },
      { key: 'channel_id',     label: 'Default Channel ID', type: 'text',     help: 'Slack channel ID (e.g. C0123ABCDEF)' },
      { key: 'app_id',         label: 'App ID',             type: 'text',     help: 'Your Slack App ID' },
    ],
    webhook: `${CHAT_SVC}/chat/webhooks/slack`,
  },
  sms: {
    label:   'SMS (Twilio)',
    icon:    MessageSquare,
    color:   '#f22f46',
    fields: [
      { key: 'account_sid',  label: 'Account SID',      type: 'text',     help: 'From Twilio console dashboard' },
      { key: 'auth_token',   label: 'Auth Token',       type: 'password', help: 'From Twilio console dashboard' },
      { key: 'from_number',  label: 'From Number',      type: 'text',     help: 'Your Twilio phone number (e.g. +15551234567)' },
    ],
    webhook: `${CHAT_SVC}/chat/webhooks/sms`,
  },
  email: {
    label:   'Email',
    icon:    Mail,
    color:   '#6366f1',
    fields: [
      { key: 'smtp_host',   label: 'SMTP Host',      type: 'text',     help: 'e.g. smtp.gmail.com' },
      { key: 'smtp_port',   label: 'SMTP Port',      type: 'text',     help: 'Usually 587 for TLS' },
      { key: 'smtp_user',   label: 'SMTP Username',  type: 'text',     help: 'Email address' },
      { key: 'smtp_pass',   label: 'SMTP Password',  type: 'password', help: 'App password (not login password)' },
      { key: 'imap_host',   label: 'IMAP Host',      type: 'text',     help: 'e.g. imap.gmail.com' },
      { key: 'imap_port',   label: 'IMAP Port',      type: 'text',     help: 'Usually 993 for SSL' },
      { key: 'from_email',  label: 'From Email',     type: 'text',     help: 'Address shown to customers' },
    ],
    webhook: null,
  },
};

const SENSITIVE = new Set(['access_token','page_access_token','bot_token','auth_token','smtp_pass','app_secret','signing_secret']);

/* ── Copy button ─────────────────────────────────────────────── */
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <Button variant="outline-secondary" size="sm" onClick={doCopy} className="ms-2">
      {copied ? <CheckCircle size={13} className="text-success" /> : <Copy size={13} />}
    </Button>
  );
}

/* ══════════════════════════════════════════
   TAB 1 — Channel Integrations
══════════════════════════════════════════ */
/* ══════════════════════════════════════════
   WhatsApp — connect through Meta Embedded Signup
   Lets the tenant onboard their own number (including a WhatsApp Business app
   number via Meta's coexistence flow) instead of pasting ids and tokens.
══════════════════════════════════════════ */
function WhatsAppConnectCard({ onConnected }) {
  const [connections, setConnections] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [domains, setDomains] = useState([]);
  const [savingDomain, setSavingDomain] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  // Which onboarding Meta should offer: a fresh Cloud API number, or a number
  // that stays in the WhatsApp Business app (coexistence).
  const [keepInApp, setKeepInApp] = useState(false);
  // Optional: this organization's OWN Meta app instead of the platform's.
  const [ownApp, setOwnApp] = useState(null);      // null until loaded
  const [showOwnApp, setShowOwnApp] = useState(false);
  const [savingApp, setSavingApp] = useState(false);
  const [appMsg, setAppMsg] = useState(null);

  const loadConnections = async () => {
    setLoadingList(true);
    try {
      const data = await APICall.getT('/channels/whatsapp/embedded-signup/status');
      setConnections(data.connections || []);
    } catch {
      setConnections([]);
    } finally {
      setLoadingList(false);
    }
  };

  // Per-number actions. With several numbers on one organization the single
  // credential form below cannot manage them individually, so enable/disable and
  // remove live on each row. Removing only deletes ChatViq's configuration —
  // the number stays registered with Meta and can be added again.
  const toggleRow = async (conn) => {
    setRowBusy(conn.id);
    try {
      await APICall.postT(`/channels/${conn.id}/toggle`, {});
      await loadConnections();
      onConnected?.();
    } catch (e) {
      console.error(e);
    } finally {
      setRowBusy(null);
    }
  };

  const removeRow = async (conn) => {
    const label = conn.display_phone_number || conn.phone_number_id || 'this number';
    if (!window.confirm(
      `Remove ${label} from ChatViq?\n\nIncoming messages to it will no longer be answered. `
      + `The number itself stays registered with Meta and can be added again.`)) return;
    setRowBusy(conn.id);
    try {
      await APICall.postT(`/channels/${conn.id}/delete`, {});
      await loadConnections();
      onConnected?.();
    } catch (e) {
      console.error(e);
    } finally {
      setRowBusy(null);
    }
  };

  const loadOwnApp = async () => {
    try {
      const d = await APICall.getT('/channels/whatsapp/embedded-signup/meta-app');
      setOwnApp({ ...d, app_secret: '' });
    } catch {
      setOwnApp({ app_id: '', config_id: '', graph_version: '', app_secret: '', app_secret_set: false, source: 'platform' });
    }
  };

  const saveOwnApp = async (clear = false) => {
    setSavingApp(true);
    setAppMsg(null);
    try {
      const body = clear ? {} : {
        app_id: (ownApp.app_id || '').trim(),
        config_id: (ownApp.config_id || '').trim(),
        graph_version: (ownApp.graph_version || '').trim(),
        enabled: true,
      };
      // Write-only: send the secret only when a new one was typed.
      if (!clear && ownApp.app_secret) body.app_secret = ownApp.app_secret;
      const res = await APICall.postT('/channels/whatsapp/embedded-signup/meta-app', body);
      setOwnApp({ ...res.settings, app_secret: '', source: res.source });
      reloadConfig();   // the Connect button may become usable straight away
      setAppMsg({ variant: 'success', msg: clear ? 'Now using the platform Meta app.' : 'Your Meta app was saved.' });
    } catch (e) {
      setAppMsg({ variant: 'danger', msg: e.message || 'Could not save the Meta app.' });
    } finally {
      setSavingApp(false);
    }
  };

  const loadDomains = async () => {
    try {
      const data = await APICall.getT('/intents/intent_domain');
      setDomains(Array.isArray(data) ? data : (data?.domains || []));
    } catch {
      setDomains([]);
    }
  };

  // Which intents a number answers with. "All topics" keeps the default
  // behaviour (the customer picks an area); choosing a domain dedicates the
  // number to it — e.g. one line for drivers and another for customers.
  const setDomain = async (conn, intent_domain) => {
    setSavingDomain(conn.id);
    try {
      await APICall.postT(`/channels/whatsapp/embedded-signup/${conn.id}/domain`, { intent_domain });
      await loadConnections();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingDomain(null);
    }
  };

  const { config, loading, busy, status, setStatus, connect, reloadConfig } = useMetaEmbeddedSignup({
    featureType: keepInApp ? FEATURE_COEXISTENCE : undefined,
    onConnected: (res) => { loadConnections(); onConnected?.(res); },
  });

  useEffect(() => { loadConnections(); loadDomains(); loadOwnApp(); }, []);

  if (loading) return null;

  return (
    <Card className="border-0 rounded-4 shadow-sm mb-3" style={{ background: 'rgba(37,211,102,.06)' }}>
      <Card.Body className="p-3">
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div>
            <div className="d-flex align-items-center gap-2">
              <Link2 size={18} style={{ color: '#25d366' }} />
              <h6 className="mb-0 fw-bold">Connect with Meta</h6>
            </div>
            <div className="text-muted small mt-1">
              Sign in with Facebook and pick your number — Meta sets up the WhatsApp Business
              Account, and existing WhatsApp Business app numbers can stay in the app
              (coexistence). No ids or tokens to copy by hand.
            </div>
          </div>
          <div className="text-end">
            <Button size="sm" className="primaryBtn" onClick={connect} disabled={busy || !config?.enabled}>
              {busy ? <><Spinner size="sm" className="me-1" />Connecting…</> : <><Plus size={14} className="me-1" />Connect WhatsApp</>}
            </Button>
            <Form.Check
              type="checkbox"
              id="cvq-keep-in-app"
              className="small text-muted mt-2"
              checked={keepInApp}
              disabled={busy || !config?.enabled}
              onChange={e => setKeepInApp(e.target.checked)}
              label="Number stays in the WhatsApp Business app"
            />
          </div>
        </div>

        {!config?.enabled && (
          <Alert variant="secondary" className="py-2 small mb-0 mt-3">
            {config?.turned_off ? (
              <>Embedded Signup is switched off. A ChatViq super-admin can enable it in
                <b> Management → Billing &amp; Plans → WhatsApp Embedded Signup</b>.</>
            ) : (
              <>
                Embedded Signup isn't set up yet — missing{' '}
                <b>{(config?.missing || []).map(m => ({
                  app_id: 'Meta App ID',
                  app_secret: 'App Secret',
                  config_id: 'Embedded Signup Config ID',
                }[m] || m)).join(', ') || 'configuration'}</b>.{' '}
                A ChatViq super-admin sets this once in <b>Management → Billing &amp; Plans →
                WhatsApp Embedded Signup</b>, or this organization can use its own Meta app below.
              </>
            )}
            <div className="mt-1">You can still connect a number manually with the fields underneath.</div>
          </Alert>
        )}

        {status && (
          <Alert variant={status.variant} dismissible onClose={() => setStatus(null)} className="py-2 small mb-0 mt-3">
            {status.msg}
          </Alert>
        )}

        <div className="mt-3 pt-2 border-top">
          <Button variant="link" size="sm" className="p-0 text-decoration-none"
            onClick={() => setShowOwnApp(v => !v)}>
            {showOwnApp ? '▾' : '▸'} Use your own Meta app (advanced)
            {ownApp?.source === 'organization' && <Badge bg="info" className="ms-2">Active</Badge>}
          </Button>
          {showOwnApp && ownApp && (
            <div className="mt-2">
              <div className="text-muted small mb-2">
                By default this organization connects through ChatViq's Meta app — nothing to fill in.
                Enter your own app only if your business must own the Meta app that holds your
                WhatsApp Business Account. The secret is stored encrypted and never shown again.
              </div>
              {appMsg && (
                <Alert variant={appMsg.variant} dismissible onClose={() => setAppMsg(null)} className="py-2 small">
                  {appMsg.msg}
                </Alert>
              )}
              <Row className="g-2">
                <Col md={4}>
                  <Form.Label className="fw-semibold small">Meta App ID</Form.Label>
                  <Form.Control size="sm" value={ownApp.app_id || ''}
                    onChange={e => setOwnApp(p => ({ ...p, app_id: e.target.value }))} placeholder="1234567890123456" />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold small">Embedded Signup Config ID</Form.Label>
                  <Form.Control size="sm" value={ownApp.config_id || ''}
                    onChange={e => setOwnApp(p => ({ ...p, config_id: e.target.value }))} placeholder="1588572569673601" />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold small">Graph version</Form.Label>
                  <Form.Control size="sm" value={ownApp.graph_version || ''}
                    onChange={e => setOwnApp(p => ({ ...p, graph_version: e.target.value }))} placeholder="v21.0" />
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-semibold small">
                    {ownApp.app_secret_set ? 'App Secret (leave blank to keep current)' : 'App Secret'}
                  </Form.Label>
                  <Form.Control size="sm" type="password" value={ownApp.app_secret || ''}
                    onChange={e => setOwnApp(p => ({ ...p, app_secret: e.target.value }))}
                    placeholder={ownApp.app_secret_set ? '•••••••• (unchanged)' : 'Meta App → Settings → Basic'} />
                </Col>
              </Row>
              <div className="d-flex gap-2 mt-2">
                <Button size="sm" className="primaryBtn" onClick={() => saveOwnApp(false)} disabled={savingApp}>
                  {savingApp ? <Spinner size="sm" /> : 'Save my Meta app'}
                </Button>
                {ownApp.source === 'organization' && (
                  <Button size="sm" variant="outline-secondary" onClick={() => saveOwnApp(true)} disabled={savingApp}>
                    Use ChatViq's app instead
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {!loadingList && connections.length > 0 && (
          <div className="mt-3">
            <div className="fw-semibold small mb-2">Connected numbers</div>
            <Table size="sm" className="mb-0 align-middle">
              <thead>
                <tr className="text-muted" style={{ fontSize: 11 }}>
                  <th>Number</th><th>Name</th><th>Type</th><th>Answers with</th><th>Phone number ID</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {connections.map(c => (
                  <tr key={c.id} style={{ fontSize: 12 }}>
                    <td className="fw-semibold">{c.display_phone_number || '—'}</td>
                    <td>{c.verified_name || '—'}</td>
                    <td>
                      <Badge bg={c.connection_type === 'coexistence' ? 'info' : 'secondary'}>
                        {c.connection_type === 'coexistence' ? 'Coexistence' : 'Cloud API'}
                      </Badge>
                    </td>
                    <td style={{ minWidth: 150 }}>
                      <Form.Select
                        size="sm"
                        value={c.intent_domain || ''}
                        disabled={savingDomain === c.id}
                        onChange={e => setDomain(c, e.target.value)}
                      >
                        <option value="">All topics</option>
                        {domains.map(d => (
                          <option key={d.id || d.name} value={d.name}>{d.name}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td><code style={{ fontSize: 11 }}>{c.phone_number_id || '—'}</code></td>
                    <td>
                      <Badge bg={c.is_enabled ? 'success' : 'secondary'}>
                        {c.is_enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant={c.is_enabled ? 'outline-warning' : 'outline-success'}
                        className="me-1 py-0" disabled={rowBusy === c.id} onClick={() => toggleRow(c)}>
                        {rowBusy === c.id ? <Spinner size="sm" /> : (c.is_enabled ? 'Disable' : 'Enable')}
                      </Button>
                      <Button size="sm" variant="outline-danger" className="py-0"
                        disabled={rowBusy === c.id} onClick={() => removeRow(c)} title="Remove from ChatViq">
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function ChannelsTab() {
  const { showToast, ToastContainer } = useToast();
  const [configs,   setConfigs]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [active,    setActive]    = useState('whatsapp');
  const [creds,     setCreds]     = useState({});
  const [saving,    setSaving]    = useState(false);
  const [testing,   setTesting]   = useState(false);
  const [testTo,    setTestTo]    = useState('');
  const [toggling,  setToggling]  = useState(false);
  const [alert,     setAlert]     = useState(null);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const data = await APICall.getT(`/channels`);
      const map  = {};
      (data.channels || []).forEach(c => { map[c.channel_type] = c; });
      setConfigs(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  useEffect(() => {
    setAlert(null);
    setTestTo('');
    const existing = configs[active];
    setCreds(existing?.credentials ? { ...existing.credentials } : {});
  }, [active, configs]);

  const meta     = CHANNELS[active];
  const existing = configs[active];

  const save = async () => {
    setSaving(true);
    setAlert(null);
    try {
      // Send the row's id when one is loaded: WhatsApp rows are matched by
      // phone_number_id, so editing that very field would otherwise look like a
      // brand-new number and add a second channel instead of updating this one.
      await APICall.postT(`/channels`, {
        channel_type: active,
        credentials: creds,
        ...(existing?.id ? { channel_id: existing.id } : {}),
      });
      setAlert({ variant: 'success', msg: 'Configuration saved.' });
      fetchConfigs();
    } catch (e) {
      setAlert({ variant: 'danger', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnable = async () => {
    if (!existing?.id) return;
    setToggling(true);
    try {
      await APICall.postT(`/channels/${existing.id}/toggle`, {});
      fetchConfigs();
    } catch (e) {
      setAlert({ variant: 'danger', msg: e.message });
    } finally {
      setToggling(false);
    }
  };

  const deleteChannel = async () => {
    if (!existing?.id || !window.confirm('Delete this channel configuration?')) return;
    try {
      await APICall.postT(`/channels/${existing.id}/delete`, {});
      fetchConfigs();
    } catch (e) {
      setAlert({ variant: 'danger', msg: e.message });
    }
  };

  const testChannel = async () => {
    if (!existing?.id) return;
    setTesting(true);
    try {
      const data = await APICall.postT(`/channels/${existing.id}/test`, { to: testTo });
      setAlert({ variant: data.success ? 'success' : 'danger', msg: data.success ? 'Test message sent!' : data.error });
    } catch (e) {
      setAlert({ variant: 'danger', msg: e.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="p-3">
      <Row className="g-3">
        {/* Channel selector sidebar */}
        <Col md={3}>
          <div className="d-flex flex-column gap-2">
            {Object.entries(CHANNELS).map(([type, meta]) => {
              const cfg = configs[type];
              return (
                <div
                  key={type}
                  onClick={() => setActive(type)}
                  className={`p-3 rounded-3 d-flex align-items-center gap-2 cursor-pointer
                    ${active === type ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : 'bg-light border border-transparent'}`}
                  style={{ cursor: 'pointer' }}
                >
                  <meta.icon size={20} style={{ color: meta.color }} />
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold small text-truncate">{meta.label}</div>
                    {cfg ? (
                      <Badge bg={cfg.is_enabled ? 'success' : 'secondary'} className="small">
                        {cfg.is_enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    ) : (
                      <span className="text-muted" style={{ fontSize: 11 }}>Not set up</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Col>

        {/* Config panel */}
        <Col md={9}>
          <Card className="border-0 rounded-4 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  {meta?.icon && <meta.icon size={24} style={{ color: meta.color }} />}
                  <div>
                    <h6 className="mb-0 fw-bold">{meta?.label}</h6>
                    {existing && (
                      <Badge bg={existing.is_enabled ? 'success' : 'secondary'}>
                        {existing.is_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    )}
                  </div>
                </div>
                {existing && (
                  <div className="d-flex gap-2">
                    <Button size="sm" variant={existing.is_enabled ? 'outline-warning' : 'outline-success'}
                      onClick={toggleEnable} disabled={toggling}>
                      {toggling ? <Spinner size="sm" /> : existing.is_enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={deleteChannel}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                )}
              </div>

              {alert && <Alert variant={alert.variant} dismissible onClose={() => setAlert(null)} className="py-2">{alert.msg}</Alert>}

              {active === 'whatsapp' && <WhatsAppConnectCard onConnected={fetchConfigs} />}

              {/* Webhook URL */}
              {meta?.webhook && (
                <Alert variant="info" className="py-2 small mb-3">
                  <strong>Webhook URL</strong> — set this in your provider console:
                  <div className="d-flex align-items-center mt-1 gap-1">
                    <code className="flex-grow-1 text-break">{meta.webhook}</code>
                    <CopyBtn text={meta.webhook} />
                  </div>
                </Alert>
              )}

              {/* Credential fields */}
              <Row className="g-3">
                {meta?.fields.map(f => (
                  <Col md={6} key={f.key}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">{f.label}</Form.Label>
                      <Form.Control
                        type={f.type}
                        placeholder={f.help}
                        value={creds[f.key] || ''}
                        onChange={e => setCreds(p => ({ ...p, [f.key]: e.target.value }))}
                        size="sm"
                      />
                      <Form.Text className="text-muted" style={{ fontSize: 11 }}>{f.help}</Form.Text>
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              <div className="d-flex align-items-center gap-2 mt-4 flex-wrap">
                <Button className="primaryBtn" size="sm" onClick={save} disabled={saving}>
                  {saving ? <><Spinner size="sm" className="me-1" />Saving…</> : 'Save Configuration'}
                </Button>

                {existing?.id && active !== 'facebook' && (
                  <>
                    <Form.Control
                      size="sm"
                      style={{ width: 180 }}
                      placeholder={active === 'email' ? 'test@email.com' : '+15551234567'}
                      value={testTo}
                      onChange={e => setTestTo(e.target.value)}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={testChannel} disabled={testing}>
                      {testing ? <Spinner size="sm" /> : 'Send Test'}
                    </Button>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

/* ══════════════════════════════════════════
   SDK snippet viewer — HTML / React / Vue / NPM
══════════════════════════════════════════ */
const SDK_TABS = [
  { key: 'html',  label: 'HTML',  ext: 'html' },
  { key: 'react', label: 'React', ext: 'jsx'  },
  { key: 'vue',   label: 'Vue',   ext: 'vue'  },
  { key: 'npm',   label: 'NPM',   ext: 'txt'  },
];

function SnippetBox({ snippets, keyName = 'widget', onCopied }) {
  const available = SDK_TABS.filter(t => snippets?.[t.key]);
  const [type, setType] = useState(available[0]?.key || 'html');
  const current = SDK_TABS.find(t => t.key === type) || SDK_TABS[0];
  const code = snippets?.[type] || '';

  const copy = () => {
    navigator.clipboard.writeText(code);
    onCopied?.();
  };

  const download = () => {
    const safe = String(keyName || 'widget').replace(/\s+/g, '-').toLowerCase();
    // For HTML, download a ready-to-open page; for others, the raw snippet file.
    const contents = type === 'html'
      ? `<!doctype html>\n<html lang="en">\n<head>\n  <meta charset="utf-8" />\n  <meta name="viewport" content="width=device-width, initial-scale=1" />\n  <title>ChatViq Chat Widget — ${keyName}</title>\n</head>\n<body>\n  ${code}\n</body>\n</html>\n`
      : code;
    const blob = new Blob([contents], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chatviq-widget-${safe}.${current.ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="d-flex gap-1 mb-2 flex-wrap">
        {available.map(t => (
          <Button
            key={t.key}
            size="sm"
            variant={t.key === type ? 'primary' : 'outline-secondary'}
            onClick={() => setType(t.key)}
          >
            {t.label}
          </Button>
        ))}
      </div>
      <pre className="bg-white p-2 rounded small text-break mb-2 border" style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto' }}>
        {code}
      </pre>
      <div className="d-flex gap-2">
        <Button size="sm" variant="success" onClick={copy}>
          <Copy size={13} className="me-1" /> Copy {current.label}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={download}>
          <Download size={13} className="me-1" /> Download
        </Button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   TAB 2 — Embeddable Widget Keys
══════════════════════════════════════════ */
function WidgetTab() {
  const { showToast, ToastContainer: WidgetToast } = useToast();
  const [keys,    setKeys]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating]  = useState(false);
  const [alert,   setAlert]   = useState(null);
  const [newKey,  setNewKey]  = useState(null);
  const [domains, setDomains] = useState([]);
  const [sdkData, setSdkData] = useState(null); // { key, snippets } for the SDK modal
  const [form,    setForm]    = useState({ key_name: 'Website Widget', allowed_origins: '*', intent_domain: '' });

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const data = await APICall.getT(`/widget/keys`);
      setKeys(data.keys || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Intent domains populate the "Scope to domain" dropdown. A key bound to a
  // domain opens sessions pre-scoped to it, so visitors skip the domain menu.
  const fetchDomains = async () => {
    try {
      const data = await APICall.getT(`/intents/intent_domain`);
      setDomains(Array.isArray(data) ? data : (data?.domains || []));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchKeys(); fetchDomains(); }, []);

  const createKey = async () => {
    setCreating(true);
    setAlert(null);
    try {
      const data = await APICall.postT(`/widget/keys`, form);
      setNewKey(data);
      setShowModal(false);
      setForm({ key_name: 'Website Widget', allowed_origins: '*', intent_domain: '' });
      fetchKeys();
    } catch (e) {
      setAlert({ variant: 'danger', msg: e.message });
    } finally {
      setCreating(false);
    }
  };

  // Open the SDK modal for a key: fetch the snippet set (HTML/React/Vue/NPM)
  // so the user can pick their integration type, then copy or download it.
  const openSdk = async (k) => {
    try {
      const data = await APICall.getT(`/widget/keys/${k.id}`);
      setSdkData({
        key: k,
        snippets: data.sdk_snippets || { html: data.embed_snippet || '' },
      });
    } catch (e) {
      showToast(e.message || 'Failed to load SDK.', 'danger');
    }
  };

  const rotateKey = async (id) => {
    if (!window.confirm('Rotate this key? The old key will stop working immediately.')) return;
    try {
      const data = await APICall.postT(`/widget/keys/${id}/rotate`, {});
      setNewKey(data);
      fetchKeys();
    } catch (e) {
      showToast(e.message || 'Failed to rotate key.', 'danger');
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm('Delete this widget key?')) return;
    try {
      await APICall.postT(`/widget/keys/${id}/delete`, {});
      if (newKey?.key?.id === id) setNewKey(null);
      fetchKeys();
      showToast('Key deleted.', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to delete key.', 'danger');
    }
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h6 className="fw-bold mb-0">Embeddable Widget Keys</h6>
          <small className="text-muted">Generate an API key to embed ChatViq on any website</small>
        </div>
        <Button className="primaryBtn" size="sm" onClick={() => setShowModal(true)}>
          + Create Key
        </Button>
      </div>

      {/* New key snippet */}
      {newKey && (
        <Alert variant="success" className="mb-3">
          <Alert.Heading className="fs-6">Widget key ready — choose your SDK and copy the snippet</Alert.Heading>
          <SnippetBox
            snippets={newKey.sdk_snippets || { html: newKey.embed_snippet }}
            keyName={newKey.key?.key_name}
            onCopied={() => showToast('Snippet copied.', 'success')}
          />
          <Button size="sm" variant="outline-secondary" className="mt-2" onClick={() => setNewKey(null)}>
            Dismiss
          </Button>
        </Alert>
      )}

      {keys.length === 0 ? (
        <div className="text-center text-muted py-5">
          <Key size={40} className="mb-2 text-muted" />
          <div>No widget keys yet. Create one to embed the chat widget on your website.</div>
        </div>
      ) : (
        <Table hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>API Key</th>
              <th>Domain</th>
              <th>Allowed Origins</th>
              <th>Last Used</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id}>
                <td className="fw-semibold">{k.key_name}</td>
                <td><code className="small">{k.api_key}</code></td>
                <td className="small text-muted">{k.intent_domain || <span className="text-secondary">All</span>}</td>
                <td className="small text-muted">{k.allowed_origins || '*'}</td>
                <td className="small text-muted">
                  {k.last_used_at ? formatDate(k.last_used_at) : 'Never'}
                </td>
                <td className="text-end">
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openSdk(k)} title="Get SDK / embed code">
                    <Code size={13} className="me-1" />SDK
                  </Button>
                  <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => rotateKey(k.id)} title="Rotate key">
                    <RefreshCw size={13} />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => deleteKey(k.id)} title="Delete key">
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create key modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Create Widget Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {alert && <Alert variant={alert.variant} className="py-2">{alert.msg}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small">Key Name</Form.Label>
            <Form.Control placeholder="e.g. Website Widget"
              value={form.key_name}
              onChange={e => setForm(p => ({ ...p, key_name: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold small">Intent Domain</Form.Label>
            <Form.Select
              value={form.intent_domain}
              onChange={e => setForm(p => ({ ...p, intent_domain: e.target.value }))}>
              <option value="">All domains — let the visitor choose</option>
              {domains.map(d => (
                <option key={d.id ?? d.name} value={d.name}>{d.name}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Pin this widget to one domain so visitors skip the “which area?” menu, or leave as All domains.
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-semibold small">Allowed Origins</Form.Label>
            <Form.Control placeholder="* or https://yoursite.com,https://app.yoursite.com"
              value={form.allowed_origins}
              onChange={e => setForm(p => ({ ...p, allowed_origins: e.target.value }))} />
            <Form.Text className="text-muted">Use * to allow all origins, or specify comma-separated domains.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="primaryBtn" size="sm" onClick={createKey} disabled={creating}>
            {creating ? <><Spinner size="sm" className="me-1" />Creating…</> : 'Create Key'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SDK / embed-code modal */}
      <Modal show={!!sdkData} onHide={() => setSdkData(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">
            Embed code — {sdkData?.key?.key_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">
            Choose your integration type. React and Vue use the <code>@chatviq/react</code> / <code>@chatviq/vue</code> packages; HTML drops a script tag on any site.
          </p>
          {sdkData && (
            <SnippetBox
              snippets={sdkData.snippets}
              keyName={sdkData.key?.key_name}
              onCopied={() => showToast('Snippet copied.', 'success')}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setSdkData(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <WidgetToast />
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function Channels() {
  return (
    <TabComponent
      pageContent={{
        title:    'Channels',
        subTitle: 'Connect WhatsApp, Facebook, Slack, SMS, Email, and embed the chat widget',
        tabs: [
          { tabTitle: 'Channel Integrations', tabKey: 'channels', tabContent: <ChannelsTab /> },
          { tabTitle: 'Embeddable Widget',    tabKey: 'widget',   tabContent: <WidgetTab /> },
        ],
      }}
    />
  );
}
